import { useEffect, useState } from "react";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { format } from "date-fns";
import { Plus, Eye, Trash, Truck, Pencil, DownloadSimple, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { isValidGstin, lookupGstin, normalizeGstin } from "@/lib/gstinLookup";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"];

const emptyForm = { party_name: "", party_address: "", party_city: "", party_state: "Maharashtra", party_pincode: "", party_gstin: "", party_phone: "", challan_date: new Date().toISOString().split("T")[0], notes: "", transport_mode: "", vehicle_no: "" };

export default function Challans() {
  const [challans, setChallans] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({...emptyForm});
  const [gstinMsg, setGstinMsg] = useState(null);

  const onPartyGstinChange = async (raw) => {
    const g = normalizeGstin(raw);
    setForm((f) => ({ ...f, party_gstin: g }));
    if (g.length < 15) { setGstinMsg(null); return; }
    if (!isValidGstin(g)) { setGstinMsg({ tone: "text-rose-600", text: "Invalid GSTIN format" }); return; }
    setGstinMsg({ tone: "text-blue-600", text: "Fetching from GST records…" });
    try {
      const d = await lookupGstin(g);
      if (d.found) {
        setForm((f) => ({
          ...f,
          party_name: d.name || f.party_name,
          party_address: d.address || f.party_address,
          party_city: d.city || f.party_city,
          party_state: d.state || f.party_state,
          party_pincode: d.pincode || f.party_pincode,
          party_phone: d.phone || f.party_phone,
        }));
        setGstinMsg({ tone: "text-emerald-700", text: `✓ ${d.name}${d.status ? ` (${d.status})` : ""} — auto-filled` });
      } else {
        setForm((f) => ({ ...f, party_state: d.state || f.party_state }));
        setGstinMsg({ tone: "text-amber-700", text: d.message || "Only state auto-filled" });
      }
    } catch {
      setGstinMsg({ tone: "text-rose-600", text: "Lookup failed" });
    }
  };
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("list");
  const [year, setYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState("");

  const dfv = useDateFilterVersion();
  useEffect(() => { fetchData(); }, [dfv]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [chRes, pRes] = await Promise.all([axios.get(`${API}/challans`), axios.get(`${API}/products`)]);
      setChallans(chRes.data); setProducts(pRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const addItem = () => setItems([...items, { product_id: "", product_name: "", hsn_sac: "", quantity: 1, price: 0, gst_rate: 0, amount: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (index, field, value) => {
    const ni = [...items]; ni[index][field] = value;
    if (field === "product_id") { const p = products.find(pr => pr.id === value); if (p) { ni[index].product_name = p.name; ni[index].hsn_sac = p.hsn_sac; ni[index].price = p.price; ni[index].gst_rate = p.gst_rate; ni[index].amount = ni[index].quantity * p.price; } }
    if (field === "quantity" || field === "price") ni[index].amount = ni[index].quantity * ni[index].price;
    setItems(ni);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { alert("Add at least one item"); return; }
    try {
      if (editing) { await axios.put(`${API}/challans/${editing}`, { ...form, items }); }
      else { await axios.post(`${API}/challans`, { ...form, items }); }
      setDialogOpen(false); setEditing(null); setForm({...emptyForm}); setItems([]);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (ch) => {
    setEditing(ch.id);
    setForm({ party_name: ch.party_name, party_address: ch.party_address || "", party_city: ch.party_city || "", party_state: ch.party_state || "Maharashtra", party_pincode: ch.party_pincode || "", party_gstin: ch.party_gstin || "", party_phone: ch.party_phone || "", challan_date: ch.challan_date, notes: ch.notes || "", transport_mode: ch.transport_mode || "", vehicle_no: ch.vehicle_no || "" });
    setItems(ch.items || []);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this challan?")) return;
    try { await axios.delete(`${API}/challans/${id}`); fetchData(); } catch { alert("Failed"); }
  };

  const handleStatusChange = async (id, status) => {
    try { await axios.put(`${API}/challans/${id}/status`, { status }); fetchData(); } catch { alert("Failed"); }
  };

  const handleDownloadPdf = async (id, no) => {
    try {
      const res = await axios.get(`${API}/challans/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", `${no}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { alert("Failed"); }
  };

  const handleExport = async (type) => {
    try {
      const params = reportMonth ? `month=${reportMonth}` : `year=${year}`;
      const res = await axios.get(`${API}/challans/report/excel?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", `Challans_${reportMonth || year}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { alert("Export failed"); }
  };

  const fmt = (v) => `\u20b9${(v||0).toFixed(2)}`;

  return (
    <div className="p-4 sm:p-8" data-testid="challans-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-1">Challans</h1>
          <p className="text-slate-500 text-sm">Delivery challans with monthly/annual tracking</p>
        </div>
        <button onClick={() => { setEditing(null); setForm({...emptyForm}); setItems([]); setDialogOpen(true); }} data-testid="create-challan-btn" className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 flex items-center gap-2">
          <Plus size={20} weight="bold" /> New Challan
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-sm w-fit">
        {[{k:"list",l:"Challans"},{k:"export",l:"Export"}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-semibold rounded-sm transition-all ${tab===t.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "list" && (
        <div className="bg-white border border-slate-200 rounded-sm">
          {loading ? <div className="p-12 animate-pulse"><div className="h-40 bg-slate-200 rounded"></div></div> : challans.length === 0 ? (
            <div className="p-12 text-center">
              <Truck size={64} weight="duotone" className="mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">No challans yet</h3>
              <button onClick={() => setDialogOpen(true)} className="px-6 py-3 bg-blue-600 text-white rounded-sm font-semibold">Create Challan</button>
            </div>
          ) : (
            <div className="overflow-x-auto sticky-table">
              <table className="w-full">
                <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Challan No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Party</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {challans.map((ch) => (
                    <tr key={ch.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{ch.challan_no}</td>
                      <td className="px-4 py-3"><p className="text-sm font-semibold text-slate-900">{ch.party_name}</p><p className="text-xs text-slate-500">{ch.party_city}, {ch.party_state}</p></td>
                      <td className="px-4 py-3 text-sm text-slate-600">{format(new Date(ch.challan_date), "dd MMM yyyy")}</td>
                      <td className="px-4 py-3 text-sm font-semibold">{fmt(ch.subtotal)}</td>
                      <td className="px-4 py-3">
                        <select value={ch.status} onChange={(e) => handleStatusChange(ch.id, e.target.value)} data-testid={`challan-status-${ch.id}`} className={`px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider border-0 cursor-pointer ${ch.status === "completed" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                          <option value="pending">Pending</option>
                          <option value="completed">Completed</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => handleEdit(ch)} data-testid={`edit-challan-${ch.id}`} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-sm"><Pencil size={16} /></button>
                          <button onClick={() => handleDownloadPdf(ch.id, ch.challan_no)} className="px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded-sm">PDF</button>
                          <button onClick={() => handleDelete(ch.id)} data-testid={`delete-challan-${ch.id}`} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-sm"><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "export" && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <h2 className="text-lg font-bold text-slate-900">Export Challan Data</h2>
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <Label className="mb-1 block text-xs">By Year</Label>
              <div className="flex items-center gap-2">
                <button onClick={() => setYear(year-1)} className="p-1.5 border border-slate-200 rounded-sm"><CaretLeft size={16} /></button>
                <span className="font-bold min-w-[50px] text-center">{year}</span>
                <button onClick={() => setYear(year+1)} className="p-1.5 border border-slate-200 rounded-sm"><CaretRight size={16} /></button>
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Or By Month</Label>
              <input type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="px-3 py-1.5 border border-slate-200 rounded-sm text-sm" />
            </div>
            <div className="flex gap-2 pt-4">
              <button onClick={handleExport} data-testid="export-challans-excel" className="px-3 py-2 bg-green-600 text-white rounded-sm text-xs font-semibold hover:bg-green-700 flex items-center gap-1"><DownloadSimple size={16} /> Excel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">{editing ? "Edit" : "New"} Delivery Challan</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Party Name *</Label><Input data-testid="challan-party-name" autoCap value={form.party_name} onChange={(e) => setForm({...form, party_name: e.target.value})} required /></div>
              <div><Label>Phone</Label><Input data-testid="challan-party-phone" value={form.party_phone} onChange={(e) => setForm({...form, party_phone: e.target.value})} /></div>
              <div className="sm:col-span-2"><Label>Address *</Label><Input data-testid="challan-party-address" autoCap value={form.party_address} onChange={(e) => setForm({...form, party_address: e.target.value})} required /></div>
              <div><Label>City *</Label><Input data-testid="challan-party-city" autoCap value={form.party_city} onChange={(e) => setForm({...form, party_city: e.target.value})} required /></div>
              <div><Label>State *</Label><select data-testid="challan-party-state" value={form.party_state} onChange={(e) => setForm({...form, party_state: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" required>{STATES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><Label>Pincode *</Label><Input data-testid="challan-party-pincode" value={form.party_pincode} onChange={(e) => setForm({...form, party_pincode: e.target.value})} required /></div>
              <div>
                <Label>GSTIN</Label>
                <Input data-testid="challan-party-gstin" value={form.party_gstin} maxLength={15} placeholder="15-digit GSTIN auto-fetches" onChange={(e) => onPartyGstinChange(e.target.value)} />
                {gstinMsg && <p data-testid="challan-gstin-status" className={`mt-1 text-[11px] font-semibold ${gstinMsg.tone}`}>{gstinMsg.text}</p>}
              </div>
              <div><Label>Date *</Label><Input type="date" data-testid="challan-date" value={form.challan_date} onChange={(e) => setForm({...form, challan_date: e.target.value})} required /></div>
              <div><Label>Transport</Label><Input data-testid="challan-transport" value={form.transport_mode} onChange={(e) => setForm({...form, transport_mode: e.target.value})} placeholder="Road, Rail" /></div>
              <div><Label>Vehicle No</Label><Input data-testid="challan-vehicle" value={form.vehicle_no} onChange={(e) => setForm({...form, vehicle_no: e.target.value})} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3"><Label>Items</Label><button type="button" onClick={addItem} data-testid="challan-add-item" className="px-2 py-1 bg-blue-600 text-white rounded-sm text-xs font-semibold flex items-center gap-1"><Plus size={14} /> Add</button></div>
              {items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-2">
                  <select value={item.product_id} onChange={(e) => updateItem(idx, "product_id", e.target.value)} className="flex-1 px-2 py-1.5 border border-slate-200 rounded-sm text-sm" required>
                    <option value="">Product</option>{products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <Input type="number" className="w-16" step="0.01" min="0.01" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} />
                  <Input type="number" className="w-20" step="0.01" value={item.price} onChange={(e) => updateItem(idx, "price", parseFloat(e.target.value) || 0)} placeholder="Price" />
                  <span className="text-sm text-slate-500 min-w-[70px] text-right">{fmt(item.amount)}</span>
                  <button type="button" onClick={() => removeItem(idx)} className="p-1 text-red-500"><Trash size={16} /></button>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Add at least one item</p>}
            </div>
            <div><Label>Notes</Label><Textarea data-testid="challan-notes" autoCap value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} /></div>
            <DialogFooter className="gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="save-challan-btn">{editing ? "Update" : "Create"} Challan</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
